import {
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  Event as EventIcon,
  LocalFireDepartment as LocalFireDepartmentIcon,
  MusicNote as MusicNoteIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  Container,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CharCounter from "../components/CharCounter";
import ChecklistDocumentosUpload from "../components/ChecklistDocumentosUpload";
import autorizacoesService from "../services/autorizacoes";
import { tipoAutorizacaoEnum } from "../utils/constants";
import { CLASSIFICACAO_USO_FOGO_CONTROLADO } from "../utils/usoFogoControladoClassificacao";

const TIPO_ICONS = {
  0: <MusicNoteIcon sx={{ fontSize: 32 }} />,
  1: <LocalFireDepartmentIcon sx={{ fontSize: 32 }} />,
};

const TIPO_DESC = {
  0: "Autorização para uso de equipamento de som em eventos públicos ou privados.",
  1: CLASSIFICACAO_USO_FOGO_CONTROLADO.descricaoAtividade,
};

const TIPO_USO_FOGO_CONTROLADO = 1;

const steps = ["Tipo de Autorização", "Dados do Evento", "Documentos", "Revisão"];

const NOME_MAX = 30;
const OBS_MAX = 1000;

function NovaAutorizacao() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [tipoAutorizacao, setTipoAutorizacao] = useState("");
  const [checklist, setChecklist] = useState(null);
  const [loadingChecklist, setLoadingChecklist] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [documentosFiles, setDocumentosFiles] = useState({});
  const [docErros, setDocErros] = useState({});

  const [form, setForm] = useState({
    nomeEvento: "",
    dataEvento: "",
    horaEvento: "",
    localEvento: "",
    observacoes: "",
  });

  const hoje = useMemo(() => new Date().toISOString().slice(0, 10), []);

  useEffect(() => {
    if (tipoAutorizacao === "") {
      setChecklist(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingChecklist(true);
        setError("");
        const data = await autorizacoesService.getChecklistPorTipo(
          tipoAutorizacao
        );
        if (cancelled) return;
        if (!data) {
          setChecklist(null);
          setError(
            "Nenhum checklist ativo encontrado para este tipo de autorização."
          );
        } else {
          setChecklist(data);
        }
      } catch (err) {
        if (!cancelled)
          setError(err?.message || "Erro ao carregar checklist.");
      } finally {
        if (!cancelled) setLoadingChecklist(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tipoAutorizacao]);

  const podeAvancarStep1 = tipoAutorizacao !== "" && checklist && !loadingChecklist;

  const podeAvancarStep2 =
    form.nomeEvento.trim() &&
    form.nomeEvento.length <= NOME_MAX &&
    form.dataEvento &&
    form.dataEvento >= hoje &&
    form.horaEvento &&
    form.localEvento.trim() &&
    (form.observacoes?.length ?? 0) <= OBS_MAX;

  const podeAvancarStep2Docs = useMemo(
    () =>
      (checklist?.itens ?? []).every(
        (it) => !it.obrigatorio || !!documentosFiles[`checklist_doc_${it.id}`]
      ),
    [checklist, documentosFiles]
  );

  const handleNext = () => {
    setError("");
    if (activeStep === 0 && !podeAvancarStep1) return;
    if (activeStep === 1 && !podeAvancarStep2) {
      setError("Preencha todos os campos obrigatórios corretamente.");
      return;
    }
    if (activeStep === 2 && !podeAvancarStep2Docs) {
      const erros = {};
      (checklist?.itens ?? []).forEach((it) => {
        if (it.obrigatorio && !documentosFiles[`checklist_doc_${it.id}`]) {
          erros[`checklist_doc_${it.id}`] = "Documento obrigatório não enviado";
        }
      });
      setDocErros(erros);
      setError("Envie todos os documentos obrigatórios antes de continuar.");
      return;
    }
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => {
    setError("");
    setActiveStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError("");
      const payload = {
        tipoAutorizacao: Number(tipoAutorizacao),
        nomeEvento: form.nomeEvento,
        dataEvento: `${form.dataEvento}T${form.horaEvento}:00`,
        localEvento: form.localEvento,
        observacoes: form.observacoes || null,
      };
      const result = await autorizacoesService.criarSolicitacao(payload);
      const novaId = result?.data?.id;

      if (novaId) {
        const uploads = Object.entries(documentosFiles)
          .filter(([, arquivo]) => Boolean(arquivo))
          .map(([key, arquivo]) => {
            const itemId = key.replace("checklist_doc_", "");
            const item = checklist?.itens?.find(
              (it) => String(it.id) === itemId
            );
            const descricao = item?.descricao || arquivo.name;
            const extensao = arquivo.name.split(".").pop();
            const novoNome = `${descricao}.${extensao}`;
            const arquivoRenomeado = new File([arquivo], novoNome, {
              type: arquivo.type,
            });
            return autorizacoesService.uploadDocumentoAutorizacao(
              novaId,
              arquivoRenomeado
            );
          });
        await Promise.allSettled(uploads);
        setSuccess("Solicitação enviada com sucesso.");
        setTimeout(() => navigate(`/autorizacao/${novaId}`), 800);
      } else {
        setSuccess("Solicitação enviada com sucesso.");
        setTimeout(() => navigate("/autorizacoes"), 800);
      }
    } catch (err) {
      setError(err?.message || "Erro ao enviar solicitação.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 4,
          flexDirection: { xs: "column", sm: "row" },
          textAlign: { xs: "center", sm: "left" },
          gap: 2,
        }}
      >
        <AssignmentTurnedInIcon
          sx={{ fontSize: { xs: 28, md: 36 }, color: theme.palette.primary.main, mr: { sm: 2 } }}
        />
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 700, lineHeight: 1.1, fontSize: { xs: "1.1rem", md: "1.5rem" } }}
          >
            Nova Autorização
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Preencha os dados abaixo para solicitar uma autorização
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          borderRadius: 2,
          overflowX: "auto",
        }}
      >
        <Stepper
          activeStep={activeStep}
          orientation={isMobile ? "vertical" : "horizontal"}
          alternativeLabel={!isMobile}
          sx={{
            "& .MuiStepLabel-label": {
              fontSize: { xs: "0.7rem", sm: "0.875rem" },
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          borderRadius: 2,
        }}
      >
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Selecione o tipo de autorização
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Escolha o tipo adequado para o seu evento. O checklist de
              documentação será carregado automaticamente.
            </Typography>
            <Grid container spacing={2}>
              {tipoAutorizacaoEnum.map((t) => {
                const selected = String(tipoAutorizacao) === String(t.value);
                return (
                  <Grid item xs={12} sm={6} key={t.value}>
                    <Card
                      elevation={0}
                      sx={{
                        border: `2px solid ${
                          selected
                            ? theme.palette.primary.main
                            : alpha(theme.palette.divider, 0.8)
                        }`,
                        borderRadius: 2,
                        backgroundColor: selected
                          ? alpha(theme.palette.primary.main, 0.05)
                          : "transparent",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.03
                          ),
                        },
                      }}
                    >
                      <CardActionArea
                        onClick={() => setTipoAutorizacao(t.value)}
                        sx={{ p: 2 }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Box
                            sx={{
                              color: selected
                                ? theme.palette.primary.main
                                : "text.secondary",
                              display: "flex",
                            }}
                          >
                            {TIPO_ICONS[t.value] ?? (
                              <EventIcon sx={{ fontSize: 32 }} />
                            )}
                          </Box>
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 600,
                                color: selected
                                  ? theme.palette.primary.main
                                  : "text.primary",
                              }}
                            >
                              {t.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {TIPO_DESC[t.value] ?? ""}
                            </Typography>
                          </Box>
                          {selected && (
                            <CheckCircleIcon
                              sx={{
                                ml: "auto",
                                color: theme.palette.primary.main,
                              }}
                            />
                          )}
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {Number(tipoAutorizacao) === TIPO_USO_FOGO_CONTROLADO && (
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Potencial poluidor-degradador: {CLASSIFICACAO_USO_FOGO_CONTROLADO.potencial}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Classificação por {CLASSIFICACAO_USO_FOGO_CONTROLADO.unidade} (referência
                  — não altera o formulário):
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Porte</TableCell>
                        <TableCell>Faixa</TableCell>
                        <TableCell>Código</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {CLASSIFICACAO_USO_FOGO_CONTROLADO.faixas.map((faixa) => (
                        <TableRow key={faixa.codigo}>
                          <TableCell>{faixa.porte}</TableCell>
                          <TableCell>{faixa.descricao}</TableCell>
                          <TableCell>
                            <strong>{faixa.codigo}</strong>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}

            {loadingChecklist && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 3 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Carregando checklist...
                </Typography>
              </Box>
            )}

            {checklist && !loadingChecklist && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.success.main, 0.06),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <CheckCircleIcon
                  sx={{ color: theme.palette.success.main, fontSize: 20 }}
                />
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: theme.palette.success.dark }}
                  >
                    Checklist encontrado: {checklist.nome}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {checklist.itens?.length || 0} documento(s) exigido(s)
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Dados do Evento
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Preencha as informações do evento para o qual deseja solicitar a
              autorização.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Nome do Evento"
                  fullWidth
                  required
                  value={form.nomeEvento}
                  inputProps={{ maxLength: NOME_MAX }}
                  onChange={(e) =>
                    setForm({ ...form, nomeEvento: e.target.value })
                  }
                  helperText="Nome curto e descritivo do evento"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={form.nomeEvento}
                          maxLength={NOME_MAX}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Data do Evento"
                  type="date"
                  fullWidth
                  required
                  value={form.dataEvento}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: hoje }}
                  onChange={(e) =>
                    setForm({ ...form, dataEvento: e.target.value })
                  }
                  helperText="A data não pode ser anterior a hoje"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Horário"
                  type="time"
                  fullWidth
                  required
                  value={form.horaEvento}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) =>
                    setForm({ ...form, horaEvento: e.target.value })
                  }
                  helperText="Hora de início"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Local do Evento"
                  fullWidth
                  required
                  value={form.localEvento}
                  onChange={(e) =>
                    setForm({ ...form, localEvento: e.target.value })
                  }
                  helperText="Endereço ou nome do local"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Observações (opcional)"
                  fullWidth
                  multiline
                  rows={4}
                  value={form.observacoes}
                  inputProps={{ maxLength: OBS_MAX }}
                  onChange={(e) =>
                    setForm({ ...form, observacoes: e.target.value })
                  }
                  helperText="Informações adicionais sobre o evento"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={form.observacoes}
                          maxLength={OBS_MAX}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Documentação
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Anexe os documentos exigidos pelo checklist. Itens marcados como{" "}
              <strong>obrigatórios</strong> devem ser enviados para continuar.
            </Typography>
            <ChecklistDocumentosUpload
              itensChecklist={checklist?.itens ?? []}
              documentos={documentosFiles}
              documentosChecklistFiles={{}}
              documentosErros={docErros}
              onFileChange={(itemId, arquivo) => {
                setDocumentosFiles((prev) => ({
                  ...prev,
                  [`checklist_doc_${itemId}`]: arquivo,
                }));
                setDocErros((prev) => {
                  const next = { ...prev };
                  delete next[`checklist_doc_${itemId}`];
                  return next;
                });
              }}
            />
          </Box>
        )}

        {activeStep === 3 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Revise os dados
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Confirme as informações antes de enviar a solicitação.
            </Typography>

            <Box
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                overflow: "hidden",
              }}
            >
              {/* Tipo */}
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  borderBottom: `1px solid ${alpha(
                    theme.palette.divider,
                    0.4
                  )}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <EventIcon
                  sx={{ color: theme.palette.primary.main, fontSize: 20 }}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {tipoAutorizacaoEnum.find(
                    (t) => t.value === Number(tipoAutorizacao)
                  )?.label ?? "—"}
                </Typography>
              </Box>

              <Grid container>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    px: 3,
                    py: 2,
                    borderBottom: {
                      xs: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                      sm: "none",
                    },
                    borderRight: {
                      sm: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                    },
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Nome do Evento
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {form.nomeEvento}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}` }}>
                  <Typography variant="caption" color="text.secondary">
                    Data e Horário do Evento
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {form.dataEvento
                      ? `${new Date(form.dataEvento + "T00:00").toLocaleDateString("pt-BR")}${form.horaEvento ? ` às ${form.horaEvento}` : ""}`
                      : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sx={{ px: 3, py: 2, borderBottom: form.observacoes ? `1px solid ${alpha(theme.palette.divider, 0.4)}` : "none" }}>
                  <Typography variant="caption" color="text.secondary">
                    Local do Evento
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {form.localEvento}
                  </Typography>
                </Grid>
                {form.observacoes && (
                  <Grid item xs={12} sx={{ px: 3, py: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Observações
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap" }}
                    >
                      {form.observacoes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>

            {Object.keys(documentosFiles).filter((k) => documentosFiles[k])
              .length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}
                >
                  <DescriptionIcon fontSize="small" />
                  Documentos a enviar
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {Object.entries(documentosFiles)
                    .filter(([, f]) => Boolean(f))
                    .map(([key, f]) => {
                      const itemId = key.replace("checklist_doc_", "");
                      const item = checklist?.itens?.find(
                        (it) => String(it.id) === itemId
                      );
                      const descricao = item?.descricao || f.name;
                      const tamanhoKb = (f.size / 1024).toFixed(0);
                      return (
                        <Box
                          key={key}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: 1.5,
                            py: 1,
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: "background.paper",
                          }}
                        >
                          <DescriptionIcon
                            fontSize="small"
                            sx={{ color: "primary.main", flexShrink: 0 }}
                          />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500, lineHeight: 1.3 }}
                              noWrap
                            >
                              {descricao}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                            >
                              {f.name} · {tamanhoKb} KB
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                </Box>
              </Box>
            )}
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          sx={{ mt: 3 }}
        >
          <Button
            disabled={activeStep === 0 || submitting}
            onClick={handleBack}
            fullWidth
            sx={{ textTransform: "none", width: { xs: "100%", sm: "auto" } }}
          >
            Voltar
          </Button>
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              fullWidth
              sx={{
                textTransform: "none",
                minWidth: { sm: 120 },
                width: { xs: "100%", sm: "auto" },
              }}
              disabled={
                (activeStep === 0 && !podeAvancarStep1) ||
                (activeStep === 1 && !podeAvancarStep2) ||
                (activeStep === 2 && !podeAvancarStep2Docs)
              }
            >
              Próximo
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              fullWidth
              sx={{
                textTransform: "none",
                minWidth: { sm: 160 },
                fontWeight: 600,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              {submitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Confirmar e Enviar"
              )}
            </Button>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}

export default NovaAutorizacao;
